import React, { FC } from 'react';
import styled from 'styled-components/macro';
import { useFieldArray } from 'react-hook-form';
import * as MESSAGES from 'constants/messages';
import theme from 'styles/theme';
import { MAX_NEW_GRADUATE_STUDY_ABROAD_EXPERIENCES } from 'constants/profile';
import {
  ProfileFormControl,
  ProfileFormRegister,
  ProfileFormValues,
  ProfileFormMethods,
} from 'types/profile';
import { AddField } from 'components/atoms';
import { getPeriodErrorMessage } from 'utils/profile/fields';
import { CountryNameFormField, StudyAbroadPeriodFormField } from './SubFields';

const Wrapper = styled.div``;

const FieldsContainer = styled.div<{ isLast: boolean }>`
  display: flex;
  margin-bottom: ${({ isLast }) => (isLast ? 0 : '24px')};
  padding-bottom: ${({ isLast }) => (isLast ? 0 : '16px')};
  border-bottom: ${({ isLast }) =>
    isLast ? 'none' : `1px solid ${theme.borderDefault}`};
`;

const Fields = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const RemoveStudyAbroadExperience = styled.div`
  align-self: flex-end;
  margin-top: 6px;
  color: ${theme.basePrimary};
  font-size: 12px;
  line-height: 1.5;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

type Value = {
  newGraduate: Pick<ProfileFormValues['newGraduate'], 'studyAbroadExperiences'>;
};

type NewGraduateStudyAbroadExperiencesFormFieldProps = {
  errors: NonNullable<
    ProfileFormMethods<Value>['errors']['newGraduate']
  >['studyAbroadExperiences'];
  register: ProfileFormRegister<Value>;
  control: ProfileFormControl<Value>;
  trigger: ProfileFormMethods<Value>['trigger'];
  touched: NonNullable<
    ProfileFormMethods<Value>['formState']['touched']['newGraduate']
  >['studyAbroadExperiences'];
};

const NewGraduateStudyAbroadExperiencesFormField: FC<NewGraduateStudyAbroadExperiencesFormFieldProps> = props => {
  const { register, control, trigger, errors, touched } = props;

  const { fields, append, remove } = useFieldArray<
    ProfileFormValues['newGraduate']['studyAbroadExperiences'][0]
  >({
    control,
    name: 'newGraduate.studyAbroadExperiences',
  });

  return (
    <Wrapper>
      {fields.map((field, index) => (
        <FieldsContainer
          key={field?.id ?? index}
          isLast={index === MAX_NEW_GRADUATE_STUDY_ABROAD_EXPERIENCES - 1}
        >
          <Fields data-testid="new-graduate-study-abroad-experience-fields">
            <CountryNameFormField
              register={register}
              index={index}
              defaultValue={field.countryName}
            />

            <StudyAbroadPeriodFormField
              control={control}
              trigger={trigger}
              index={index}
              error={getPeriodErrorMessage(
                {
                  startYear: errors?.[index]?.startYear,
                  startMonth: errors?.[index]?.startMonth,
                  endYear: errors?.[index]?.endYear,
                  endMonth: errors?.[index]?.endMonth,
                },
                {
                  startYear: touched?.[index]?.startYear,
                  startMonth: touched?.[index]?.startMonth,
                  endYear: touched?.[index]?.endYear,
                  endMonth: touched?.[index]?.endMonth,
                },
              )}
              defaultValues={{
                startYear: field.startYear,
                startMonth: field.startMonth,
                endYear: field.endYear,
                endMonth: field.endMonth,
              }}
            />

            {index > 0 && (
              <RemoveStudyAbroadExperience
                onClick={() => remove(index)}
                data-testid="remove-study-abroad-experience"
              >
                {MESSAGES.COMMON.BUTTON.REMOVE_FIELD}
              </RemoveStudyAbroadExperience>
            )}
          </Fields>
        </FieldsContainer>
      ))}

      {fields.length < MAX_NEW_GRADUATE_STUDY_ABROAD_EXPERIENCES && (
        <AddField
          style={{ marginTop: '-8px' }}
          onClick={() => append({})}
          data-testid="add-study-abroad-experience"
        />
      )}
    </Wrapper>
  );
};

export default NewGraduateStudyAbroadExperiencesFormField;
